import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { Building2, Users, Send, Phone, Mail } from 'lucide-react';
import './Employers.css';
import employerImg from '../../assets/EmployersPictures/employersImg1.jpg';

const Employers = () => {
    const [formData, setFormData] = useState({
        companyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        staffNeeded: '',
        message: '',
        nickname: ''
    });

    // --- Message state success/error ---
    const [status, setStatus] = useState({ type: '', msg: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: 'info', msg: 'Sending...' });
        setIsSubmitting(true);

        const messageData = {
            name: formData.contactPerson,
            email: formData.email,
            type: 'employer',
            staffNeeded: formData.staffNeeded,
            subject: `Employer Inquiry: ${formData.companyName}`,
            message: `Company: ${formData.companyName}\nPhone: ${formData.phone}\nStaff Needed: ${formData.staffNeeded}\n\nMessage: ${formData.message}`
        };

        try {
            const [nodeResponse, phpResponse] = await Promise.allSettled([
                fetch('/api-portal/contact', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(messageData)
                }),
                fetch('/send-employer.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(formData)
                })
            ]);

            const nodeOk = nodeResponse.status === 'fulfilled' && nodeResponse.value.ok;
            const phpOk = phpResponse.status === 'fulfilled' && phpResponse.value.ok;

            if (nodeOk && phpOk) {
                setStatus({ type: 'success', msg: 'Request sent successfully!' });
                setFormData({ companyName: '', contactPerson: '', email: '', phone: '', staffNeeded: '', message: '', nickname: '' });
            } else if (nodeOk) {
                setStatus({ type: 'warning', msg: 'Email failed to send. Please contact us manually.' });
            } else {
                throw new Error("Major system failure");
            }
        } catch (error) {
            setStatus({ type: 'danger', msg: 'Error sending request. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="employer-us-page">
            {/* Section 1: Hero */}
            <section
                className="employer-hero-section"
                style={{ backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0)), url(${employerImg})` }}
            >
                <Container className="h-100 d-flex align-items-center justify-content-center">
                    <div className="employer-hero-content text-center text-white">
                        <h1 className="employer-title mb-3">Find Talent</h1>
                        <p className="employer-subtitle">
                            We're always looking for quality employers to add to our selection. Let's schedule a sit down.
                        </p>
                        <div className="employer-divider mx-auto mb-3"></div>
                    </div>
                </Container>
            </section>

            {/* Section 2: Employer Form */}
            <section className="employer-form-section py-5">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={8}>
                            <div className="employer-form-card shadow-lg animate-in">
                                <div className="form-header text-center mb-5">
                                    <h2 className="fw-bold">Hire with <span className="text-primary">INFINITI</span></h2>
                                    <p className="text-muted">Fill out the form below and let us know what kind of talent you need.</p>
                                </div>

                                {/* --- Show message --- */}
                                {status.msg && (
                                    <Alert variant={status.type} className="mb-4 text-center">
                                        {status.msg}
                                    </Alert>
                                )}

                                <Form onSubmit={handleSubmit}>
                                    {/* Honeypot */}
                                    <div className="hp-field" style={{ display: 'none' }}>
                                        <input
                                            type="text"
                                            name="nickname"
                                            value={formData.nickname}
                                            onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                                            tabIndex="-1"
                                            autoComplete="off"
                                        />
                                    </div>

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-4">
                                                <Form.Label className="fw-bold"><Building2 size={16} className="me-2 text-primary" />Company Name</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Your Company LLC"
                                                    value={formData.companyName}
                                                    onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-4">
                                                <Form.Label className="fw-bold"><Users size={16} className="me-2 text-primary" />Contact Person</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Full Name"
                                                    value={formData.contactPerson}
                                                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-4">
                                                <Form.Label className="fw-bold"><Mail size={16} className="me-2 text-primary" />Business Email</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    placeholder="email@company.com"
                                                    value={formData.email}
                                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-4">
                                                <Form.Label className="fw-bold"><Phone size={16} className="me-2 text-primary" />Phone Number</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="(555) 000-0000"
                                                    value={formData.phone}
                                                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                                    required
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-bold">What type of staff are you looking for?</Form.Label>
                                        <Form.Select
                                            value={formData.staffNeeded}
                                            onChange={(e) => setFormData({ ...formData, staffNeeded: e.target.value })}
                                            required
                                        >
                                            <option value="">Select Category...</option>

                                            <option value="Accounting">Accounting</option>
                                            <option value="Administrative Assistants">Administrative Assistants</option>
                                            <option value="CDL A and B Drivers">CDL A and B Drivers</option>
                                            <option value="Forklift Operators">Forklift Operators</option>
                                            <option value="General Labor">General Labor</option>
                                            <option value="Manufacturing">Manufacturing</option>
                                            <option value="Order Selectors">Order Selectors</option>
                                            <option value="Transportation">Transportation</option>
                                            <option value="Warehouse Associates">Warehouse Associates</option>
                                            <option value="Welders">Welders</option>
                                            <option value="Other">Other</option>
                                        </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="fw-bold">Specific Requirements / Message</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            placeholder="Tell us more about the roles you need to fill..."
                                            value={formData.message}
                                            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                        />
                                    </Form.Group>

                                    <div className="text-center">
                                        <Button
                                            type="submit"
                                            className="btn-employer-submit"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                                            {!isSubmitting && <Send size={18} className="ms-2" />}
                                        </Button>
                                    </div>
                                </Form>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default Employers;